import { useEffect, useMemo, useState } from 'react';
import { api, type User } from '../api.ts';
import SavedCostings from './SavedCostings.tsx';
import { logoUrl, useCompany, type Company } from '../company.ts';
import { guardUnsaved } from '../lib/unsaved.ts';
import {
  COSTING_DEFAULTS,
  COSTING_FIELDS,
  COSTING_NUMBER_FIELDS,
  camelToSnake,
  COST_LINES,
  MATERIAL_LABEL,
  PLY_OPTIONS,
  demyLimitsOf,
  plyLabel,
  materialsFor,
  calculateCosting,
  perBoxOf,
  readinessOf,
  totalOf,
  type CostingInputs,
  type CostingResult,
} from '../lib/costing.ts';

/**
 * Costing sheet.
 *
 * A port of the standalone HTML sheet the office had been costing boxes in
 * (`inputs/Opal-Costing-Sheet.html`), which saved into one browser's storage
 * and so existed on exactly one machine. Same fields, same arithmetic, same
 * order down the page -- the point was to move where it is kept, not to
 * redesign something people already know how to use.
 *
 * Everything on screen is worked out here as it is typed; the server only ever
 * stores the inputs. The maths lives in `lib/costing.ts` and is pinned against
 * the original sheet by `app/tests/costing.test.ts`.
 *
 * Every figure is pre-GST, like the rest of Opal. The sheet this came from
 * carried a tax rate and an inclusive total; both were removed on 17 Oct
 * (migration 044) so the rule in app/README.md holds everywhere.
 */

/** A row as the API returns it: the columns, plus the names and dates. */
interface CostingRow extends Record<string, unknown> {
  id: number;
  company_name: string;
  box_name: string;
  /** Tells two costings of the same box apart. '' is the unnamed first one. */
  variant: string;
  customer_id: number | null;
  box_master_id: number | null;
  updated_at: string;
  deleted_at?: string | null;
}

/** Just enough of a customer and a box master to recognise a typed name. */
interface NamedRow { id: number; name: string }

/**
 * Names match loosely, because trailing spaces and capitals are exactly how
 * the costing list and the customer list drifted apart in the first place.
 */
const sameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/** One entry in a costing's history, from the audit trail. */
interface Revision {
  audit_id: number;
  created_at: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  user_name: string;
  changed_fields: string[];
  /** The whole row as it stood after this change -- enough to recost it. */
  new_values: Record<string, unknown> | null;
}

/** The form holds strings so a field can be empty rather than stuck at 0. */
type FormValues = Record<keyof CostingInputs, string>;

/** What Opal charges today, keyed by the field it fills. */
type RateRow = { key: string; label: string; unit: string; value: string };
type Rates = Partial<Record<keyof CostingInputs, number>>;

const ratesOf = (rows: RateRow[]): Rates =>
  Object.fromEntries(rows.map((r) => [r.key, Number(r.value)])) as Rates;

/**
 * A fresh sheet.
 *
 * Rates come from `costing_rates` so the office can change its own prices;
 * COSTING_DEFAULTS is what is left when they have not arrived yet, which is
 * the first instant after the page opens and the whole time if the request
 * fails. A sheet with blank rates is still perfectly usable -- they can be
 * typed, exactly as they were before this existed.
 */
const blankForm = (rates: Rates = {}): FormValues =>
  Object.fromEntries(
    COSTING_FIELDS.map((k) => {
      const v = rates[k] ?? COSTING_DEFAULTS[k];
      return [k, v === 0 ? '' : String(v)];
    }),
  ) as FormValues;

/** The material field for a liner position; flutes have none. */
const materialKey = (position: number) =>
  `plyMaterial${position}` as keyof CostingInputs;
const gsmKey = (position: number) => `plyGsm${position}` as keyof CostingInputs;
/** Liner positions only -- a flute has no material to clear. */
const MATERIAL_AT_POSITION: Record<number, keyof CostingInputs | undefined> = {
  1: 'plyMaterial1', 3: 'plyMaterial3', 5: 'plyMaterial5', 7: 'plyMaterial7',
};

/** A saved row back into the form. */
/**
 * How a saved costing is named in the picker.
 *
 * Box first: that is what anyone searching remembers, and it is what the two
 * dropdowns this replaced made you supply last.
 */
const labelOf = (c: CostingRow) =>
  `${c.box_name}${c.variant ? ` (${c.variant})` : ''} — ${c.company_name}`;

/** Everything a person could have typed, in one comparable string. */
const snapshot = (f: FormValues, company: string, box: string, variant: string) =>
  JSON.stringify([company.trim(), box.trim(), variant.trim(),
                  ...COSTING_FIELDS.map((k) => f[k])]);

const formFromRow = (row: CostingRow): FormValues =>
  Object.fromEntries(
    COSTING_FIELDS.map((k) => {
      const raw = row[camelToSnake(k)];
      if (typeof COSTING_DEFAULTS[k] === 'string') {
        return [k, String(raw ?? COSTING_DEFAULTS[k])];
      }
      // NUMERIC arrives as a string: "230.00" should show as 230, not 230.00.
      const v = raw === null || raw === undefined ? 0 : Number(raw);
      // Ply is a real choice, not a blank-when-zero figure.
      if (k === 'ply') return [k, String(v || COSTING_DEFAULTS.ply)];
      return [k, v === 0 ? '' : String(v)];
    }),
  ) as FormValues;

/**
 * The form holds every field as a string; the maths wants numbers for most of
 * them and the material names as they are. Driven off the defaults rather
 * than a second list, so a new field cannot be forgotten here.
 */
const toInputs = (f: FormValues): CostingInputs =>
  Object.fromEntries(
    COSTING_FIELDS.map((k) => {
      if (typeof COSTING_DEFAULTS[k] === 'string') return [k, f[k] || COSTING_DEFAULTS[k]];
      const v = parseFloat(f[k]);
      return [k, Number.isFinite(v) ? v : 0];
    }),
  ) as unknown as CostingInputs;

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const inr2 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rs = (n: number) => `₹${inr.format(Math.round(n))}`;
const perBox = (v: number | null) => (v === null ? '–' : `₹${inr2.format(v)}`);
const when = (iso?: string | null) =>
  !iso ? '' : new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * A column name as a person would say it. The audit trail names columns, and
 * "flute_gsm_2" in a history row is worse than no history at all.
 */
const FIELD_WORDS: Record<string, string> = {
  // 'gst' stays although the column is gone: revisions saved before 17 Oct
  // still name gst_pct in their changed_fields, and history must stay readable.
  gsm: 'GSM', ccp: 'CCP', gst: 'GST', dd: 'Double Demy', demy: 'Demy',
  in: '(inch)', pct: '%', qty: 'quantity', wbb: 'white back board',
};
function prettyField(column: string): string {
  // A ply column carries its position last -- ply_gsm_1 -- which reads as
  // "Ply GSM 1". The position belongs with the ply it numbers.
  const ply = /^ply_(.+)_(\d)$/.exec(column);
  if (ply) return `Ply ${ply[2]} ${(FIELD_WORDS[ply[1]!] ?? ply[1])}`;

  const words = column.split('_').map((w) => FIELD_WORDS[w] ?? w);
  const s = words.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const whenExact = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

/**
 * What a revision's figures worked out to.
 *
 * Recomputed from the inputs saved at the time, through the same
 * `calculateCosting` the live sheet uses -- so a historical figure can never
 * disagree with the current one, and no cost was ever stored to go stale.
 */
function revisionCost(rev: Revision): number | null {
  if (!rev.new_values) return null;
  // Through perBoxOf, so a revision saved before the sheet refused to price
  // an incomplete costing does not now show a confident figure for one.
  return perBoxOf(toInputs(formFromRow(rev.new_values as unknown as CostingRow)));
}

/** Whatever the person who made the change said about it. */
const revisionNote = (rev: Revision): string =>
  String(rev.new_values?.change_note ?? '').trim();

/** The change, in the words someone would use for it. */
function revisionSummary(rev: Revision): string {
  if (rev.action === 'INSERT') return 'Created';
  if (rev.action === 'DELETE') return 'Removed from the database';

  // The note explains the change, so it is not itself one of the changes.
  const fields = (rev.changed_fields ?? []).filter((f) => f !== 'change_note');
  // A soft delete and a restore are both ordinary updates to deleted_at.
  if (fields.includes('deleted_at')) {
    return rev.new_values?.deleted_at ? 'Deleted' : 'Restored';
  }
  if (fields.length === 0) return 'Saved with no change';

  const shown = fields.slice(0, 3).map(prettyField);
  return fields.length > 3
    ? `${shown.join(', ')} and ${fields.length - 3} more`
    : shown.join(', ');
}

/** Breakdown colours, one per line, stable regardless of which rows show. */
const LINE_COLOURS = COST_LINES.map(
  (_, i) => `hsl(${Math.round((i * 360) / COST_LINES.length + 130) % 360} 52% 52%)`,
);

/**
 * One numeric field.
 *
 * Declared out here, not inside the page. A component defined during render is
 * a new component type on every render, so React tears the input down and
 * builds it again on each keystroke -- which loses the caret after a single
 * character. The page calls these through small helpers instead, so the
 * element type stays the same between renders.
 */
function NumField({ field, label, unit, hint, value, max, onChange }: {
  field: keyof CostingInputs;
  label: string;
  unit?: string;
  hint?: string;
  value: string;
  max?: number;
  onChange: (k: keyof CostingInputs, v: string) => void;
}) {
  // Nothing on this sheet can be negative -- not a size, not a rate, not a
  // quantity. `min` stops the spinner and marks the field invalid; it does not
  // stop a minus sign being typed, which is why the page checks as well.
  const bad = value.trim() !== '' && !(Number(value) >= 0)
    || (max !== undefined && Number(value) > max);

  return (
    <div className="field">
      <label htmlFor={`c_${field}`}>
        {label}{unit && <span className="dim" style={{ fontWeight: 400 }}> ({unit})</span>}
      </label>
      <input id={`c_${field}`} type="number" inputMode="decimal" value={value}
             min={0} max={max} aria-invalid={bad || undefined}
             style={bad ? { borderColor: 'var(--late)' } : undefined}
             onChange={(e) => onChange(field, e.target.value)} />
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

/**
 * The board, drawn to scale, with its own dimensions written on it.
 *
 * Ported from the source sheet's `drawSheet`. It is worth the space: the
 * numbers above it are all abstractions, and a sheet entered as 2 × 30 instead
 * of 20 × 30 looks obviously wrong as a shape long before anybody notices the
 * cost per box is out by a factor of ten.
 *
 * Drawn in centimetres purely so the numbers in the viewBox stay sane -- the
 * scale is set by the viewBox, so the units cancel. Strokes are
 * non-scaling-vector so a long thin sheet does not get a hairline border.
 */
function SheetDiagram({ heightIn, lengthIn }: { heightIn: number; lengthIn: number }) {
  const w = lengthIn * 2.54;
  const h = heightIn * 2.54;
  if (!(w > 0 && h > 0)) return null;

  const fs = Math.max(w, h) * 0.045;
  const inset = fs * 0.4;
  const label = (inches: number, cm: number) =>
    `${inches % 1 ? inches.toFixed(2) : inches} in  (${cm.toFixed(1)} cm)`;

  return (
    <svg viewBox={`${-fs * 3.2} ${-fs * 0.8} ${w + fs * 4.4} ${h + fs * 3.6}`}
         style={{ width: '100%', maxHeight: 280, marginTop: 10 }}
         role="img"
         aria-label={`Board ${heightIn} by ${lengthIn} inches, drawn to scale`}>
      <rect x={0} y={0} width={w} height={h}
            fill="var(--accent-soft)" stroke="var(--accent)" strokeWidth={2}
            vectorEffect="non-scaling-stroke" />
      <rect x={inset} y={inset}
            width={Math.max(w - 2 * inset, 0)} height={Math.max(h - 2 * inset, 0)}
            fill="none" stroke="var(--accent)" strokeWidth={1} strokeDasharray="5 4"
            vectorEffect="non-scaling-stroke" />

      <line x1={0} y1={h + fs * 0.9} x2={w} y2={h + fs * 0.9}
            stroke="var(--muted)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <text x={w / 2} y={h + fs * 2.3} textAnchor="middle" fontSize={fs} fill="var(--ink)">
        {label(lengthIn, w)}
      </text>

      <line x1={-fs * 0.9} y1={0} x2={-fs * 0.9} y2={h}
            stroke="var(--muted)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      <text x={-fs * 1.5} y={h / 2} textAnchor="middle" fontSize={fs} fill="var(--ink)"
            transform={`rotate(-90 ${-fs * 1.5} ${h / 2})`}>
        {label(heightIn, h)}
      </text>
    </svg>
  );
}

/**
 * The costing as a document, for printing.
 *
 * Needed because the app's print stylesheet hides every `.panel` outright --
 * so without this the Print button produced a genuinely blank page. The screen
 * is a form; a printed costing has to be the opposite: no inputs, no buttons,
 * every figure written out, and the inputs it was worked out from set down
 * beside it so the sheet can be checked later without reopening the app.
 *
 * Only the size the board actually falls in is printed. Showing a Double Demy
 * column for a Demy board is how a quote goes out with the wrong number on it.
 */
/**
 * The costing as a document.
 *
 * It used to be the form in print order: a title, sixteen ruled rows of
 * whatever the screen held, and the answer as the last line of a table in
 * the same type as the die cutting charge above it. Everything was there
 * and nothing stood out.
 *
 * Three things, in the order a reader wants them. Whose box it is. What it
 * is made of and charged at, in named groups side by side rather than one
 * undifferentiated run. Then what it comes to, in a box of its own, because
 * that is the only figure anybody is looking for.
 */
function CostingPrintSheet({ companyName, boxName, variant, preparedBy, company, input, r }: {
  companyName: string;
  boxName: string;
  /** Which costing of this box -- two differ by nothing else on paper. */
  variant: string;
  /** Whoever has the sheet open. A printed figure should say whose it is. */
  preparedBy: string;
  /** Whose system made it. The sheet carried no identity of its own. */
  company: Company;
  input: CostingInputs;
  r: CostingResult;
}) {
  const side = r.size === 'DD' ? r.dd : r.demy;
  const sizeName = r.size === 'DD' ? 'Double Demy' : 'Demy';
  const n = (v: number) => (v ? inr2.format(v).replace(/\.00$/, '') : '–');
  const rate = (v: number, per: string) => (v ? `₹${n(v)} ${per}` : '–');

  const theBox: [string, string][] = [
    ['Sheet size (H × L)', input.boardHeightIn && input.boardLengthIn
      ? `${n(input.boardHeightIn)} × ${n(input.boardLengthIn)} in` : '–'],
    ['Costed as', r.size ? sizeName : '–'],
    ['Box quantity', n(r.boxes)],
    ['Ups', n(input.ups)],
    ['Wastage', input.wastageSheets ? `${n(input.wastageSheets)} sheets` : '–'],
    ['Sheets to run', n(r.sheets)],
  ];

  const charges: [string, string][] = [
    ['Lamination', rate(input.laminationRate, 'per 100 sq in')],
    ['Dripoff', rate(input.dripoffRate, 'per 100 sq in')],
    ['Die cutting', rate(input.dieCuttingRate, 'per 1000 sheets')],
    ['Flop pasting', rate(input.flopPastingRate, 'per box')],
  ];

  // Each layer written out, so the printed sheet can be checked against the
  // board that was actually made without reopening the app.
  const theBoard: [string, string][] = [
    ...r.layers.map((l): [string, string] => [
      // "Printed face" wrapped the label onto a second line; in a column
      // headed "The board", next to a flute and a liner, "Printed" is plain.
      `Ply ${l.position} · ${l.role === 'PRINTED' ? 'Printed'
        : l.role === 'FLUTE' ? 'Flute' : 'Liner'}`,
      `${MATERIAL_LABEL[l.material]} · ${n(l.gsm)} GSM @ ₹${n(l.rate)}/kg`
        + (l.cost ? ` · ₹${inr.format(Math.round(l.cost))}` : ''),
    ]),
    ...(r.layers.some((l) => l.role === 'FLUTE')
      ? [['Flute take-up', input.fluteExtraPct ? `${n(input.fluteExtraPct)}% of a flute's GSM` : '–'] as [string, string]]
      : []),
    ['Total board weight', r.totalWeightKg ? `${n(r.totalWeightKg)} kg` : '–'],
  ];

  const group = (title: string, rows: [string, string][]) => (
    <div className="cp-group">
      <div className="cp-group-title">{title}</div>
      {rows.map(([label, value]) => (
        <div className="cp-row" key={label}>
          <span>{label}</span><span>{value}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="print-only">
      {/* Whose sheet it is, above whose box it is about. Printed on its own
          line rather than left to the browser's page header, which is the
          page title and vanishes the moment somebody unticks "Headers and
          footers" in the print dialogue. */}
      <div className="cp-brand">
        {company.hasLogo && <img src={logoUrl(company)} alt="" />}
        <span className="cp-brand-name">{company.name}</span>
        {company.tagline && <span className="cp-brand-tag">{company.tagline}</span>}
      </div>

      <div className="cp-head">
        <div>
          <h1>{companyName || 'Costing'}</h1>
          <div className="cp-box">
            {boxName || '—'}
            {/* The variant is usually the ply ("5 ply"), so saying both gave
                "Snack carton · 5 ply · 5 ply". Whichever one there is. */}
            {variant ? <> · {variant}</> : r.size ? <> · {plyLabel(n(input.ply))}</> : null}
          </div>
        </div>
        <div className="cp-meta">
          Costing sheet<br />
          {new Date().toLocaleDateString('en-IN',
            { day: '2-digit', month: 'long', year: 'numeric' })}<br />
          Internal · pre-GST
        </div>
      </div>

      <div className="cp-cols">
        <div className="cp-col">
          {group('The box', theBox)}
          {group('Charges', charges)}
        </div>
        <div className="cp-col">{group('The board', theBoard)}</div>
      </div>

      <table className="cp-lines">
        <thead>
          <tr>
            <th>Particulars</th>
            <th className="cp-num">{r.size ? sizeName : 'Demy'} (₹)</th>
            <th className="cp-num cp-share">Share</th>
          </tr>
        </thead>
        <tbody>
          {COST_LINES.map((line, i) => {
            const v = side.lines[i] ?? 0;
            return (
              <tr key={line}>
                <td>{line}</td>
                <td className="cp-num">{inr.format(Math.round(v))}</td>
                {/* A share of nothing is not 0%, it is nothing. */}
                <td className="cp-num cp-share">
                  {side.total > 0 && v > 0 ? `${((v / side.total) * 100).toFixed(1)}%` : '–'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Both halves of the answer, at one size. The run total and the cost
          per box are the same figure divided differently; drawing one three
          times the size of the other said otherwise. */}
      <div className="cp-answer">
        <div>
          <span className="cp-label">Total · {r.size ? sizeName : 'Demy'}</span>
          <span className="cp-figure">₹{inr.format(Math.round(side.total))}</span>
        </div>
        <div>
          <span className="cp-label">Cost per box</span>
          <span className="cp-figure">{perBox(side.perBox)}</span>
        </div>
      </div>

      <div className="cp-foot">
        <span>{preparedBy ? `Prepared by ${preparedBy}` : 'Opal ERP'}</span>
        <span>
          {r.boxes > 0 ? `${inr.format(r.boxes)} boxes · ` : ''}
          {r.sheets > 0 ? `${inr.format(r.sheets)} sheets run` : ''}
        </span>
      </div>
    </div>
  );
}

/** Fills a rate from a standard value, the way the original sheet's chips did. */
function ChipButton({ field, value, label, onChange }: {
  field: keyof CostingInputs;
  value: number;
  label: string;
  onChange: (k: keyof CostingInputs, v: string) => void;
}) {
  return (
    <button type="button" className="chip"
            onClick={() => onChange(field, value === 0 ? '' : String(value))}>
      {label}
    </button>
  );
}

/**
 * Costing: the sheet and the list, one section.
 *
 * Both stay mounted. Half-typed work on the sheet has to survive a look at
 * the list and be there on the way back -- that is the whole point of being
 * able to glance at it -- and the list keeps whatever was searched or sorted
 * rather than resetting every time it is shown. It is reloaded on the way in
 * instead, so it is fresh without being forgetful.
 */
export default function Costing({ user }: { user: User }) {
  const [view, setView] = useState<'sheet' | 'saved'>('sheet');
  const [openId, setOpenId] = useState<number | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);

  const tab = (key: 'sheet' | 'saved', label: string, count?: number | null) => (
    <button type="button" aria-current={view === key}
            onClick={() => setView(key)}>
      {label}
      {count != null && <span className="seg-count">{count}</span>}
    </button>
  );

  return (
    <>
      <div className="no-print" style={{ marginBottom: 14 }}>
        <div className="seg">
          {tab('sheet', 'Costing sheet')}
          {tab('saved', 'Saved costings', savedCount)}
        </div>
      </div>

      <div style={view === 'sheet' ? undefined : { display: 'none' }}>
        <CostingSheet user={user} openId={openId} showing={view === 'sheet'} />
      </div>
      <div style={view === 'saved' ? undefined : { display: 'none' }}>
        <SavedCostings user={user} showing={view === 'saved'}
                       onCount={setSavedCount}
                       onOpen={(id) => { setOpenId(id); setView('sheet'); }} />
      </div>
    </>
  );
}

function CostingSheet({ user, openId, showing }: {
  user: User;
  /** A costing the saved list sent over to be worked on. */
  openId?: number | null;
  /** False while the list is showing, so the shortcut does not fire blind. */
  showing: boolean;
}) {
  const company = useCompany();
  const can = (p: string) => user.permissions.includes(p);
  const canSave = can('costing.save');
  const canDelete = can('costing.delete');

  const [form, setForm] = useState<FormValues>(blankForm);
  /**
   * The sheet as it last stood on the server, to compare the live one
   * against. A string rather than a flag: a flag has to be cleared by every
   * path that changes the form, and the one that forgets is the one that
   * loses somebody's afternoon.
   */
  const [lastSaved, setLastSaved] = useState<string>(() => snapshot(blankForm(), '', '', ''));
  const [companyName, setCompanyName] = useState('');
  const [boxName, setBoxName] = useState('');
  /** Which of several costings of this box. '' is the unnamed first one. */
  const [variant, setVariant] = useState('');
  /**
   * Why this save is happening. Belongs to the one revision it explains, so
   * it is cleared after saving -- carrying it forward would attach last
   * week's reason to this week's change, which is worse than no reason.
   *
   * Left out of the unsaved-changes comparison on purpose: a note with no
   * change to explain is not work worth stopping someone to protect.
   */
  const [changeNote, setChangeNote] = useState('');

  /**
   * The real customers and their box masters, so a typed name can be
   * recognised as one of them.
   *
   * Both lists are read through the customers endpoints, which every role
   * that can see a costing already has. If that ever stops being true the
   * fetch fails, the lists stay empty, and the only thing lost is the
   * suggestion -- typing a name has never been blocked and must not become
   * blocked, because costing a cold enquiry is a real thing to do.
   */
  const [customers, setCustomers] = useState<NamedRow[]>([]);
  const [boxMasters, setBoxMasters] = useState<NamedRow[]>([]);
  /** What Opal charges today, for filling a new sheet and for comparison. */
  const [rateRows, setRateRows] = useState<RateRow[]>([]);
  /** What is typed in the picker, and what it shows once one is open. */
  const [pickerText, setPickerText] = useState('');
  const [currentId, setCurrentId] = useState<number | null>(null);

  /**
   * The version of the open costing, exactly as the server sent it. Handed
   * back on save so the server can refuse to overwrite somebody else's work
   * done since -- see saveCosting in the costing module.
   */
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const [history, setHistory] = useState<Revision[]>([]);

  const [saved, setSaved] = useState<CostingRow[]>([]);
  const [deleted, setDeleted] = useState<CostingRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k: keyof CostingInputs, v: string) => setForm((p) => ({ ...p, [k]: v }));

  /**
   * Changing the ply clears the layers the board no longer has.
   *
   * The rows vanish from the screen on their own, but their figures would
   * still be sitting in the form and would go up with the next save -- which
   * the database refuses outright (ck_costings_ply_within_count), so a 5-ply
   * costing cut back to 3 would fail to save with nothing on screen to
   * explain why. Clearing them here keeps the form telling the truth.
   */
  const setPly = (value: string) => {
    const ply = Number(value) || COSTING_DEFAULTS.ply;
    setForm((prev) => {
      const next = { ...prev, ply: value };
      for (let position = ply + 1; position <= 7; position++) {
        next[gsmKey(position)] = '';
        const material = MATERIAL_AT_POSITION[position];
        if (material) next[material] = COSTING_DEFAULTS[material] as string;
      }
      return next;
    });
  };

  const r = useMemo(() => calculateCosting(toInputs(form)), [form]);
  /**
   * What this costing is for, worked out from the names rather than kept
   * beside them.
   *
   * Derived, so the two can never drift: there is no path where the name says
   * one customer and a stored id says another. Null is an ordinary answer --
   * a cold enquiry has no customer row yet and a new box has no master, and
   * neither stops the costing being made.
   */
  const customerId = useMemo(
    () => customers.find((c) => sameName(c.name, companyName))?.id ?? null,
    [customers, companyName],
  );
  const boxMasterId = useMemo(
    () => boxMasters.find((b) => sameName(b.name, boxName))?.id ?? null,
    [boxMasters, boxName],
  );

  // The masters of whichever customer is recognised; none when none is.
  useEffect(() => {
    if (customerId === null) { setBoxMasters([]); return; }
    let live = true;
    (async () => {
      try {
        const r = await api.get<{ presets: NamedRow[] }>(`/api/customers/${customerId}/presets`);
        if (live) setBoxMasters(r.presets ?? []);
      } catch {
        if (live) setBoxMasters([]);
      }
    })();
    return () => { live = false; };
  }, [customerId]);

  /**
   * Rates on this costing that are no longer what Opal charges.
   *
   * A saved costing keeps the rates it was quoted on -- that is the point of
   * snapshotting them, and they must not be rewritten when a price moves.
   * But reopening a costing from two months ago and reading its total as
   * today's figure is the quiet mistake that follows from doing the right
   * thing, so the difference is said out loud and left for a person to act on.
   */
  const staleRates = useMemo(() => {
    if (currentId === null) return [];
    return rateRows
      .filter((r) => {
        // A rate nobody has set yet has not moved -- there is nothing for
        // this costing to be out of date with. Without this, every saved
        // costing was met with a wall naming most of the rate list, purely
        // because the list had not been filled in.
        const current = Number(r.value);
        if (!(current > 0)) return false;
        // Untouched fields are not "different", they are simply unset.
        const typed = form[r.key as keyof FormValues];
        if (typed === undefined || typed.trim() === '') return false;
        return Number(typed) !== current;
      })
      .map((r) => r.label);
  }, [currentId, rateRows, form]);

  /**
   * The stale ones in a line somebody will read.
   *
   * Several rate labels contain a comma of their own -- "Plate & print,
   * Demy" -- so a comma-separated list of them says nothing about where one
   * name ends. Three at most, then a count: the point is that this costing
   * is on old rates, not which eight of them.
   */
  const staleSummary = staleRates.length <= 3
    ? staleRates.join(' · ')
    : `${staleRates.slice(0, 3).join(' · ')} and ${staleRates.length - 3} more`;

  /**
   * Whether this box is costed more than one way.
   *
   * A variant only means something next to another costing of the same box --
   * on the first one there is nothing to tell it apart from, and a third
   * field asking you to name that is clutter. After Duplicate it appears by
   * itself, because the costing it was copied from is still saved.
   */
  const hasSibling = saved.some(
    (c) => c.id !== currentId
      && sameName(c.company_name, companyName)
      && sameName(c.box_name, boxName),
  );
  const showVariant = hasSibling || variant.trim() !== '';

  /** Typing that has not reached the server yet. */
  const dirty = snapshot(form, companyName, boxName, variant) !== lastSaved;

  /**
   * Leaving with work in hand.
   *
   * There is no autosave here, and a costing that cannot yet be priced is
   * refused by Save -- so without this, a stray click on the sidebar ended
   * twenty fields of typing with nothing written down and nothing said.
   * `beforeunload` covers closing the tab and reloading; the registered
   * question covers moving around inside the app, which `beforeunload`
   * never sees. Both are cleared on unmount so no other screen inherits them.
   */
  useEffect(() => {
    if (!dirty) {
      guardUnsaved(null);
      return;
    }
    guardUnsaved(() => window.confirm(
      'This costing has changes that have not been saved. Leave the page and lose them?',
    ));
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => {
      guardUnsaved(null);
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty]);

  /**
   * Save from the keyboard.
   *
   * The Save button sits in a panel partway down a page several screens
   * long, so on a sheet being worked through it is usually out of sight.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!showing) return;
      if (!(e.key === 's' && (e.metaKey || e.ctrlKey))) return;
      e.preventDefault();
      void save();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // Deliberately every render: `save` closes over the form as it stands, so
    // a listener pinned once would save whatever was on screen when the page
    // opened.
  });

  /** The run, in its two parts: what the boxes need, and what is allowed on
   *  top for make-ready and spoilage. `sheetsFor` adds them; the Sheet panel
   *  shows the sum, so it needs the halves. */
  const wastage = toInputs(form).wastageSheets;
  const sheetsForBoxes = Math.max(0, r.sheets - (r.sheets > 0 ? wastage : 0));

  async function load() {
    try {
      const [live, gone] = await Promise.all([
        api.get<{ costings: CostingRow[] }>('/api/costings'),
        api.get<{ costings: CostingRow[] }>('/api/costings/deleted'),
      ]);
      setSaved(live.costings);
      setDeleted(gone.costings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load saved costings');
    }
  }

  async function loadHistory(id: number) {
    try {
      const res = await api.get<{ revisions: Revision[] }>(`/api/costings/${id}/history`);
      setHistory(res.revisions);
    } catch {
      // History is extra; failing to fetch it must not break the sheet.
      setHistory([]);
    }
  }

  useEffect(() => { void load(); }, []);

  /**
   * Today's rates, and filling a new sheet with them.
   *
   * The sheet is built before this arrives, so a blank one is topped up when
   * it lands -- but only a blank one. A costing already open has its own
   * rates and must keep them, and a sheet somebody has started typing into
   * must not have a figure replaced under their hands by a reply that
   * happened to be slow. Both are ruled out by asking whether anything has
   * changed since the sheet was built.
   */
  useEffect(() => {
    let live = true;
    (async () => {
      let rows: RateRow[] = [];
      try {
        const r = await api.get<{ rates: RateRow[] }>('/api/costing-rates');
        rows = r.rates ?? [];
      } catch {
        if (live) setError('Today\'s rates could not be loaded, so this sheet starts from the built-in figures. Check the rates before quoting.');
        rows = [];
      }
      if (!live || rows.length === 0) return;
      setRateRows(rows);

      const pristine = snapshot(blankForm(), '', '', '');
      setForm((current) => {
        if (snapshot(current, '', '', '') !== pristine) return current;
        const filled = blankForm(ratesOf(rows));
        setLastSaved(snapshot(filled, '', '', ''));
        return filled;
      });
    })();
    return () => { live = false; };
  }, []);

  /**
   * A costing handed over by the saved list.
   *
   * It arrives as an id before the costings themselves have loaded, so this
   * waits for the row rather than fetching it again, and opens it once. The
   * id is deliberately not cleared afterwards: leaving the sheet and coming
   * back should land on the same costing, not on a blank one.
   */
  const [handedOver, setHandedOver] = useState<number | null>(null);
  useEffect(() => {
    if (openId == null || handedOver === openId) return;
    const row = saved.find((c) => c.id === openId)
      ?? deleted.find((c) => c.id === openId);
    if (!row) return;
    // Marked as handled whatever the answer, or a declined prompt would be
    // asked again on every render.
    setHandedOver(openId);
    if (!mayReplaceSheet()) return;
    openCosting(row);
  }, [openId, handedOver, saved, deleted]);

  /** Today's rates, as the maths wants them. */
  const rates = useMemo(() => ratesOf(rateRows), [rateRows]);
  const finishRate = (key: string) => Number(rateRows.find((r) => r.key === key)?.value ?? 0);

  /**
   * The customer list, on its own errand and with its own failure.
   *
   * Kept out of `load` deliberately: no customer list means a missing
   * suggestion, not a broken screen, and a costing must still be typeable
   * without one. Wrapped rather than chained off the promise so that a
   * client which throws instead of rejecting cannot raise an error about
   * customers on a sheet whose costings loaded perfectly well.
   */
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await api.get<{ customers: NamedRow[] }>('/api/customers');
        if (live) setCustomers(r.customers ?? []);
      } catch {
        if (live) setCustomers([]);
      }
    })();
    return () => { live = false; };
  }, []);

  // Companies that already have a costing, offered under the name fields so a
  // repeat costing is picked rather than retyped into a near-miss spelling.
  const companies = useMemo(
    () => [...new Set(saved.map((c) => c.company_name))].sort((a, b) => a.localeCompare(b)),
    [saved],
  );

  /**
   * Opening a costing throws away whatever is on the sheet, so when that is
   * unsaved work it gets the same question leaving the page does. The guard
   * in lib/unsaved.ts covers navigation; this covers being replaced in
   * place, which the picker and the saved list can both do.
   */
  function mayReplaceSheet() {
    return !dirty || window.confirm(
      'This costing has changes that have not been saved. Open another and lose them?',
    );
  }

  function openCosting(row: CostingRow) {
    setForm(formFromRow(row));
    setCompanyName(row.company_name);
    setBoxName(row.box_name);
    setVariant(row.variant ?? '');
    setChangeNote('');
    setPickerText(labelOf(row));
    setLastSaved(snapshot(formFromRow(row), row.company_name, row.box_name, row.variant ?? ''));
    setCurrentId(row.id);
    setOpenedAt(row.updated_at);
    void loadHistory(row.id);
    setStatus(`Opened ${row.box_name} — last saved ${when(row.updated_at)}`);
    setError('');
  }

  /**
   * The same box again, to be costed another way.
   *
   * The commonest thing an estimator does is open the one most like this and
   * change two figures; without this it meant retyping twenty fields. It is
   * a copy on screen only -- nothing is written until Save -- and it arrives
   * needing a variant name, because the original already holds the one it
   * had and two costings of a box have to be told apart.
   */
  function duplicate() {
    setCurrentId(null);
    setOpenedAt(null);
    setHistory([]);
    setVariant('');
    setChangeNote('');
    setPickerText('');
    setLastSaved('');
    setStatus(`Copy of ${boxName} — name this variant, then save it`);
    setError('');
  }

  function newSheet() {
    setForm(blankForm(rates));
    setCompanyName('');
    setBoxName('');
    setVariant('');
    setChangeNote('');
    setPickerText('');
    setLastSaved(snapshot(blankForm(rates), '', '', ''));
    setCurrentId(null);
    setOpenedAt(null);
    setHistory([]);
    setStatus('');
    setError('');
  }

  async function save() {
    setError(''); setStatus('');
    if (!companyName.trim() || !boxName.trim()) {
      setError('Name the company and the box before saving.');
      return;
    }
    if (problems.length > 0) {
      setError(`Fix ${problems.join(', ')} before saving — nothing here can be below zero.`);
      return;
    }
    if (ready.missing.length > 0) {
      setError(`Fill in ${ready.missing.join(', ')} before saving — a costing `
        + 'that cannot be priced is not yet a costing.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{ costing: CostingRow; created: boolean }>('/api/costings', {
        companyName: companyName.trim(),
        boxName: boxName.trim(),
        variant: variant.trim(),
        customerId,
        boxMasterId,
        changeNote: changeNote.trim(),
        ...toInputs(form),
        demyMaxShortIn: demyLimitsOf(toInputs(form)).short,
        demyMaxLongIn: demyLimitsOf(toInputs(form)).long,
        // Both together or neither: their presence is what tells the server
        // this is an edit of a known version rather than a new costing.
        ...(currentId !== null && openedAt !== null
          ? { id: currentId, expectedUpdatedAt: openedAt }
          : {}),
      });
      setCurrentId(res.costing.id);
      setOpenedAt(res.costing.updated_at);
      setLastSaved(snapshot(form, companyName, boxName, variant));
      setChangeNote('');
      void loadHistory(res.costing.id);
      setStatus(res.created
        ? `Saved ${res.costing.box_name} for ${res.costing.company_name}`
        : `Updated ${res.costing.box_name} for ${res.costing.company_name}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this costing');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (currentId === null) return;
    setError(''); setStatus('');
    setBusy(true);
    try {
      await api.del(`/api/costings/${currentId}`);
      // Deleted costings keep their numbers in the list below, so nothing is
      // actually lost here -- but the sheet in front of you is now a draft.
      setCurrentId(null);
      setOpenedAt(null);
      setHistory([]);
      setStatus(`${boxName} moved to deleted costings. It can be restored below.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete this costing');
    } finally {
      setBusy(false);
    }
  }

  async function restore(row: CostingRow) {
    setError(''); setStatus('');
    setBusy(true);
    try {
      const res = await api.post<{ costing: CostingRow }>(`/api/costings/${row.id}/restore`);
      await load();

      /*
       * Put the sheet back on it, not just the row back in the list.
       *
       * Deleting detaches the sheet -- it keeps the figures but stops being
       * that costing -- so without this you were left looking at a costing
       * that exists again, on a sheet that thinks it is new. Saving then
       * collided with the very costing just restored, and said so in a
       * message about picking a different name, which is no help at all.
       *
       * Only when there is nothing to lose. Restoring something from the list
       * below while a different sheet is half typed must not throw that away;
       * in that case the row is back and can be opened from the picker.
       */
      if (dirty) {
        setStatus(`${row.box_name} restored — open it from the picker above.`);
      } else {
        openCosting(res.costing);
        setStatus(`${row.box_name} restored.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not restore that costing');
    } finally {
      setBusy(false);
    }
  }

  // Element helpers, not components -- see the note on NumField.
  const num = (field: keyof CostingInputs, label: string, unit?: string,
               hint?: string, max?: number) => (
    <NumField key={field} field={field} label={label} unit={unit} hint={hint}
              value={form[field]} max={max} onChange={set} />
  );
  const chip = (field: keyof CostingInputs, value: number, label: string) => (
    <ChipButton key={label} field={field} value={value} label={label} onChange={set} />
  );

  const sizes: ('demy' | 'dd')[] =
    r.size === 'DEMY' ? ['demy'] : r.size === 'DD' ? ['dd'] : ['demy', 'dd'];
  const SIZE_LABEL = { demy: 'Demy', dd: 'Double Demy' } as const;

  /** Whichever size the board falls in -- what the sticky bar reports. */
  const active = r.size === 'DD' ? r.dd : r.demy;

  /**
   * Fields holding a figure this sheet cannot cost.
   *
   * The arithmetic happily multiplies a negative rate and returns a smaller,
   * entirely confident number -- and nobody has to save a costing to read it
   * off the screen and quote it. So when an input is impossible the figures
   * are withheld rather than shown wrong. The server refuses these too; this
   * is about what is on screen before anyone presses Save.
   */
  const problems = COSTING_NUMBER_FIELDS
    .filter((k) => {
      const raw = form[k].trim();
      if (raw === '') return false;
      return !(Number(raw) >= 0);
    })
    .map((k) => prettyField(camelToSnake(k)));

  /**
   * What this costing still needs before it may be priced.
   *
   * See `readinessOf`: every figure here is a product, so a missing factor
   * shrinks the answer instead of breaking it. An untouched sheet is new,
   * not wrong, and is left alone; one that has been started is held back
   * until nothing it multiplies by is absent.
   */
  const ready = readinessOf(toInputs(form));

  /**
   * Two different questions, which used to be one and were wrong for it.
   *
   * `priced` asks whether there is an answer at all. A sheet nobody has
   * touched has no more of an answer than a half-typed one -- showing it the
   * flat finish charge as "Demy size Rs 200" offers a figure for a box that
   * has not been described, which is the same confident-looking wrong number
   * this screen exists to refuse.
   *
   * `missing` asks what to say about it, and says nothing on a sheet nobody
   * has started: that one is new, not unfinished.
   */
  const priced = problems.length === 0 && ready.missing.length === 0;
  const missing = ready.untouched ? [] : ready.missing;

  /** Nothing is costed, printed or saved until there is a price. */
  const blocked = !priced;

  /**
   * Figures, or a dash until there are any.
   *
   * An incomplete costing keeps the shape of the answer -- the same rows, the
   * same breakdown -- with nothing written in it. Replacing the whole panel
   * with a list of everything absent made a sheet somebody had just opened
   * look like a page of errors, when all that is true is that they have not
   * finished typing. Missing is not wrong; it is simply not yet.
   *
   * An impossible figure IS wrong, and still says so in red below.
   */
  const fig = (v: number) => (blocked ? '–' : rs(v));
  const figBox = (v: number | null) => (blocked ? '–' : perBox(v));
  const figLine = (v: number) => (blocked ? '–' : inr.format(Math.round(v ?? 0)));

  return (
    <>
      {/*
        The answer, kept in sight.

        The inputs run several screens; in the sheet this came from, the totals
        sat in a second column beside them, so the cost per box moved while you
        typed. Stacked panels lost that, and a costing sheet where you cannot
        see the cost as you work is a worse tool however tidy it looks. Three
        figures only -- the full breakdown is still down in Cost.
      */}
      {/*
        Red is kept for the one case that is actually wrong: a figure that
        cannot be true. A costing merely unfinished says so quietly, in the
        same place the price will appear, rather than turning a sheet
        somebody has just opened into a wall of warnings.
      */}
      {problems.length > 0 && (
        <div className="tag late notice no-print costing-sticky-summary"
             style={{ position: 'sticky', top: 0, zIndex: 5, marginBottom: 14,
                      padding: '12px 18px', borderRadius: 10,
                      boxShadow: '0 8px 20px rgba(0, 0, 0, .22)' }}>
          No cost while {problems.join(', ')}{' '}
          {problems.length > 1 ? 'are' : 'is'} impossible — nothing here can
          be below zero.
        </div>
      )}

      {problems.length === 0 && missing.length > 0 && !ready.untouched && (
        <div className="no-print costing-sticky-summary is-waiting"
             style={{ position: 'sticky', top: 0, zIndex: 5, marginBottom: 14,
                      background: 'var(--panel-2)', border: '1px solid var(--line)',
                      borderRadius: 10, padding: '12px 18px', textAlign: 'center',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, .22)' }}>
          <span className="dim">Waiting on {missing.join(', ')}</span>
        </div>
      )}

      {!blocked && r.size !== null && (
        <div className="no-print costing-sticky-summary"
             style={{
               position: 'sticky', top: 0, zIndex: 5, marginBottom: 14,
               // Not `.panel`: wearing the same surface as the seven panels
               // below it is exactly what made it easy to scroll past.
               background: 'var(--accent-soft)',
               border: '1px solid var(--line)',
               borderLeft: '4px solid var(--accent)',
               borderRadius: 10,
               padding: '12px 18px',
               display: 'flex', flexWrap: 'wrap',
               alignItems: 'baseline', justifyContent: 'center',
               columnGap: 26, rowGap: 6,
               boxShadow: '0 8px 20px rgba(0, 0, 0, .22)',
             }}>
          <span className="tag"
                style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}>
            {SIZE_LABEL[r.size === 'DD' ? 'dd' : 'demy']}
          </span>

          {/* The headline: the one number anybody quotes from. */}
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {perBox(active.perBox)}
            </strong>
            <span className="dim">per box</span>
          </span>

          <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: 16 }}>{rs(active.total)}</strong>
            <span className="dim">total</span>
          </span>

          {/* Sits in the row rather than pushed to the far edge, so the whole
              line reads as one centred group. */}
          {r.boxes > 0 && <span className="dim">{inr.format(r.boxes)} boxes</span>}
        </div>
      )}

      <div className="costing-layout">
        <div className="costing-main">
        {/* ------------------------------------------------ name and save */}
        {/* One panel, not two.
            Which costing this is, and what to do with it, were split across
            "Costing sheet" and "Costing details" -- two headers, two bodies
            and 577px for a picker and five buttons, with the actions divided
            between them so New sheet and Save changes sat in different
            boxes. They are one job. */}
        <div className="panel">
          <div className="panel-head">
            <h2>Costing sheet</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn" onClick={newSheet}>New sheet</button>
              {/* Nothing is printed while the costing cannot be priced -- the
                  printable document is not rendered at all -- so the button
                  that reaches it has to know that too. Left enabled, it
                  handed back a blank sheet of paper and said nothing. */}
              <button className="btn" onClick={() => window.print()} disabled={blocked}
                      title={!blocked ? ''
                        : missing.length > 0
                          ? `Nothing to print yet — still waiting on ${missing.join(', ')}.`
                          : 'Nothing to print while a figure cannot be true.'}>
                Print / save PDF
              </button>
            </div>
          </div>
          <div className="costing-body">
            <div className="hint" style={{ marginBottom: 12 }}>
              For internal reference. Every figure here is pre-GST.
            </div>

            {/*
              Always on screen, even with nothing saved yet.

              This was hidden until a costing existed, which meant the one
              feature that makes the screen worth having -- reopening what the
              office already costed -- was invisible to anyone who had not
              already used it. The source sheet showed the pickers permanently
              and said "No saved companies"; an empty control that explains
              itself beats a control that is not there.
            */}
            <div className="costing-fields wide">
              <div className="field">
                <label htmlFor="c_open">Open a saved costing</label>
                {/*
                  One box that searches, rather than a company list feeding a
                  box list. Two dependent dropdowns meant knowing the company
                  before you could look for the box, which is backwards -- the
                  box is the thing anyone remembers -- and neither could be
                  searched at all. A combobox filters on either as it is typed,
                  and keeps working when there are hundreds.
                */}
                <input id="c_open" type="text" list="costing-saved"
                       autoComplete="off"
                       disabled={saved.length === 0}
                       placeholder={saved.length
                         ? 'Search by box or company…'
                         : 'Nothing saved yet'}
                       value={pickerText}
                       onChange={(e) => {
                         const text = e.target.value;
                         const row = saved.find((c) => labelOf(c) === text);
                         // Still searching: show what is being typed.
                         if (!row) { setPickerText(text); return; }
                         // Picked one, but declined to lose the sheet: the box
                         // goes back to naming whatever is actually open, rather
                         // than sitting there claiming a costing that was not.
                         if (!mayReplaceSheet()) return;
                         openCosting(row);
                       }} />
                <datalist id="costing-saved">
                  {saved.map((c) => <option key={c.id} value={labelOf(c)} />)}
                </datalist>
                <div className="hint">
                  {saved.length === 0
                    ? 'Costings you save appear here.'
                    : `${inr.format(saved.length)} saved — type any part of a box or company name.`}
                </div>
              </div>
            </div>

            <div className="costing-fields wide">
              <div className="field">
                <label htmlFor="c_co" className="req">Company name</label>
                <input id="c_co" type="text" value={companyName} list="costing-companies"
                       onChange={(e) => setCompanyName(e.target.value)} />
                {/* Real customers first, then companies that have only ever
                    been costed. Suggesting the real name as it is typed is
                    what keeps "Amogha" and "Amogha " from becoming two. */}
                <datalist id="costing-companies">
                  {customers.map((c) => <option key={`cu${c.id}`} value={c.name} />)}
                  {companies
                    .filter((c) => !customers.some((cu) => sameName(cu.name, c)))
                    .map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="field">
                <label htmlFor="c_box" className="req">Box name</label>
                <input id="c_box" type="text" value={boxName} list="costing-boxes"
                       onChange={(e) => setBoxName(e.target.value)} />
                <datalist id="costing-boxes">
                  {boxMasters.map((b) => <option key={b.id} value={b.name} />)}
                </datalist>
              </div>
              {/* The same box costed more than one way -- "3 ply" against
                  "5 ply" -- which is the comparison choosing a board actually
                  is. Shown only once there is a second costing to tell apart
                  from; see showVariant. */}
              {showVariant && (
                <div className="field">
                  <label htmlFor="c_variant">
                    Variant
                    <span className="dim" style={{ fontWeight: 400 }}> (optional)</span>
                  </label>
                  <input id="c_variant" type="text" value={variant} maxLength={120}
                         placeholder="e.g. 3 ply"
                         onChange={(e) => setVariant(e.target.value)} />
                </div>
              )}
            </div>

            {/* Said out loud, because a costing that looks identical to
                another may or may not be attached to the customer record,
                and the difference only shows up much later. */}
            {(companyName.trim() !== '' || boxName.trim() !== '') && (
              <div className="dim" style={{ marginBottom: 10 }}>
                {customerId !== null
                  ? <>Linked to the customer <strong>{companyName.trim()}</strong></>
                  : <>Not a customer in Opal yet — costed under the name as typed</>}
                {boxMasterId !== null && <> · linked to the saved box <strong>{boxName.trim()}</strong></>}
              </div>
            )}

            {staleRates.length > 0 && (
              <div className="tag notice" style={{ marginBottom: 10 }}>
                Costed at rates that have since changed — {staleSummary}. Left
                as quoted; change them here if this costing is being redone.
              </div>
            )}

            {/* Only on a costing that already exists: the first save needs no
                explaining, and an empty box on a blank sheet is one more
                thing to read past. */}
            {canSave && currentId !== null && (
              <div className="field" style={{ marginBottom: 10 }}>
                <label htmlFor="c_note">
                  Why this changed
                  <span className="dim" style={{ fontWeight: 400 }}> (optional, saved with this revision)</span>
                </label>
                <input id="c_note" type="text" value={changeNote} maxLength={300}
                       placeholder="e.g. kraft went up to ₹48"
                       onChange={(e) => setChangeNote(e.target.value)} />
              </div>
            )}

            {error && <div className="tag late notice" style={{ marginBottom: 10 }}>{error}</div>}
            {status && !error && (
              <div className="tag ready notice" style={{ marginBottom: 10 }}>{status}</div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {canSave && (
                <button className="btn primary" onClick={save} disabled={busy}>
                  {currentId === null ? 'Save costing' : 'Save changes'}
                </button>
              )}
              {/* Said plainly here rather than only in the dialog on the way
                  out: by then the question is whether to lose the work, which
                  is a worse moment to learn there was any. */}
              {canSave && dirty && (
                <span className="dim">Unsaved changes · ⌘S</span>
              )}
              {canSave && currentId !== null && (
                <button className="btn" onClick={duplicate} disabled={busy}>
                  Duplicate
                </button>
              )}
              {canDelete && currentId !== null && (
                <button className="btn" onClick={remove} disabled={busy}>Delete this costing</button>
              )}
              {!canSave && (
                <span className="dim">You can read costings but not change them.</span>
              )}
            </div>
          </div>

        </div>

        {/* ------------------------------------------------ paper */}
        <div className="panel">
          <div className="panel-head"><h2>Sheet</h2></div>
          <div className="costing-body">
            <div className="hint" style={{ marginBottom: 12 }}>
              The sheet everything is cut from, and how many to run. What each
              layer of the board weighs is under Board.
            </div>
            {/* The board itself, then how much of it to run. Two grids rather
                than one, so the quantities always start a fresh row instead of
                trailing after the paper rate wherever the columns happen to
                wrap. */}
            <div className="costing-fields">
              {num('boardHeightIn', 'Board H', 'in')}
              {num('boardLengthIn', 'Board L', 'in')}
            </div>
            <div className="costing-fields" style={{ marginTop: 10 }}>
              {num('boxQty', 'Box quantity')}
              {num('ups', 'Ups', 'boxes per sheet')}
              {num('wastageSheets', 'Wastage', 'sheets')}
              <div className="field">
                <label htmlFor="c_sheets">
                  Sheets to run<span className="dim" style={{ fontWeight: 400 }}> (worked out)</span>
                </label>
                {/* A field, not a sentence: it belongs in the row it is
                    derived from, and reads as part of the same set of
                    figures. Disabled rather than read-only so nobody tries
                    to type into a number this screen owns. */}
                <input id="c_sheets" type="text" disabled
                       value={r.sheets ? inr.format(r.sheets) : ''} />
              </div>
            </div>
            {/* The run spelled out, because "sheets to run" is no longer the
                same number as "sheets the boxes need" and the difference is
                money. Both parts are shown, so nobody has to work out where
                the extra sheets came from. */}
            {r.sheets > 0 && (
              <div className="dim" style={{ marginTop: 4 }}>
                {inr.format(sheetsForBoxes)} for the boxes
                {sheetsForBoxes * toInputs(form).ups !== r.boxes
                  && ' (a sheet cannot be run in part)'}
                {wastage > 0 && <> + {inr.format(wastage)} wastage</>}
                {' = '}{inr.format(r.sheets)} to run. Paper, board, lamination
                and die cutting are all charged on the sheets run.
              </div>
            )}
          </div>
        </div>

        {/* ------------------------------------------------ lamination */}
        <div className="panel">
          <div className="panel-head"><h2>Lamination and dripoff</h2></div>
          <div className="costing-body">
            <div className="hint" style={{ marginBottom: 12 }}>
              Rate in ₹ per 100 sq in of sheet. The chips fill the rates set in Settings → Costing rates.
            </div>
            <div className="costing-fields">
              {num('laminationRate', 'Lamination', '₹ per 100 sq in')}
              {num('dripoffRate', 'Dripoff', '₹ per 100 sq in')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              {finishRate('glossLamination') > 0
                && chip('laminationRate', finishRate('glossLamination'), `Gloss lam ${finishRate('glossLamination')}`)}
              {finishRate('mattLamination') > 0
                && chip('laminationRate', finishRate('mattLamination'), `Matt lam ${finishRate('mattLamination')}`)}
              {finishRate('dripoff') > 0
                && chip('dripoffRate', finishRate('dripoff'), `Dripoff ${finishRate('dripoff')}`)}
              {chip('laminationRate', 0, 'No lamination')}
              {chip('dripoffRate', 0, 'No dripoff')}
            </div>
          </div>
        </div>

        {/* ------------------------------------------------ board */}
        <div className="panel">
          <div className="panel-head"><h2>Board</h2></div>
          <div className="costing-body">
            <div className="hint" style={{ marginBottom: 12 }}>
              {Number(form.ply) === 1
                ? 'A mono carton: the printed board alone, with no flute or liner.'
                : 'Ply 1 is the printed face. The even plies are the flutes, always kraft, and take up extra paper; the liners between them are kraft or white back.'}
            </div>

            {/* Rates first, then the board they price. No help text under the
                fields -- it made every column a different height and the row
                read as ragged rather than as one set of rates. */}
            <div className="costing-fields ply-row">
              <div className="field">
                <label htmlFor="c_ply">Ply</label>
                <select id="c_ply" value={form.ply}
                        onChange={(e) => setPly(e.target.value)}>
                  {PLY_OPTIONS.map((p) => (
                    <option key={p} value={p}>{p === 1 ? `1 ply · ${plyLabel(p)}` : plyLabel(p)}</option>
                  ))}
                </select>
              </div>
              {Number(form.ply) > 1 && num('fluteExtraPct', 'Flute take-up', '%')}
            </div>

            {/* The rates, all of them ₹ per kg, kept apart from the two fields
                that describe the board's shape. */}
            <div className="costing-fields" style={{ marginTop: 10 }}>
              {num('paperRate', 'Normal board', '₹ per kg')}
              {/* One paper, two names on the floor -- the sheet this came from
                  called it CCP, and it is the kraft in the board. */}
              {num('ccpRate', 'CCP / kraft', '₹ per kg')}
              {num('whiteBackRate', 'White back', '₹ per kg')}
            </div>

            {/* One row per ply, each saying what it is, what it is made of and
                what it costs. The cost column is what makes this a statement
                about the board rather than a grid of inputs. */}
            <table className="tbl costing-ply" style={{ marginTop: 14 }}>
              <thead>
                <tr>
                  <th>Layer</th>
                  <th>Material</th>
                  <th className="num" style={{ width: 120 }}>GSM</th>
                  <th className="num" style={{ width: 120 }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {r.layers.map((layer) => {
                  const isFlute = layer.role === 'FLUTE';
                  return (
                    <tr key={layer.position}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        Ply {layer.position}{' '}
                        <span className="dim">
                          ·{' '}
                          {layer.role === 'PRINTED' ? 'Printed face'
                            : isFlute ? 'Flute' : 'Liner'}
                        </span>
                      </td>
                      <td>
                        {isFlute ? (
                          // Stating the fact beats a dropdown with one option.
                          <span className="dim">{MATERIAL_LABEL[layer.material]}</span>
                        ) : (
                          <select aria-label={`Ply ${layer.position} material`}
                                  value={form[materialKey(layer.position)]}
                                  onChange={(e) =>
                                    set(materialKey(layer.position), e.target.value)}>
                            {materialsFor(layer.role).map((m) => (
                              <option key={m} value={m}>{MATERIAL_LABEL[m]}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="num">
                        <input type="number" inputMode="decimal" min={0}
                               className="num"
                               aria-label={`Ply ${layer.position} GSM`}
                               value={form[gsmKey(layer.position)]}
                               onChange={(e) =>
                                 set(gsmKey(layer.position), e.target.value)} />
                      </td>
                      <td className="num dim">
                        {layer.cost ? rs(layer.cost) : '–'}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan={2}><strong>Total</strong></td>
                  <td className="num dim">
                    {r.totalGsm ? `${inr.format(Math.round(r.totalGsm))} GSM` : '–'}
                  </td>
                  <td className="num">
                    <strong>{r.totalGsm ? rs(r.printedFace + r.boardBelowFace) : '–'}</strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/*
              A dash with no explanation reads as a broken screen.

              The board is priced by the sheet -- area x GSM x rate x sheets --
              so a full set of papers and rates still costs nothing until the
              sheet size and the quantity are in. The weight column fills in
              straight away, which makes the empty money column look all the
              more like a fault. Say which field is missing instead.
            */}
            {r.totalGsm > 0 && (r.sheetAreaM2 === 0 || r.sheets === 0) && (
              <div className="hint" style={{ marginTop: 10 }}>
                No cost yet — the board is priced by the sheet, so it needs{' '}
                {[r.sheetAreaM2 === 0 && 'a board size',
                  r.sheets === 0 && 'a box quantity and ups']
                  .filter(Boolean).join(', ')} under Sheet.
              </div>
            )}
          </div>
        </div>

      {/* ------------------------------------------------ charges */}
        <div className="panel">
          <div className="panel-head"><h2>Charges</h2></div>
          <div className="costing-body">
            <div className="hint" style={{ marginBottom: 12 }}>
              Plate and the additional charge differ between the two sizes;
              everything else is charged the same either way. Additional is
              multiplied by whichever additional rate the size selects.
              Demy is a sheet up to {demyLimitsOf(toInputs(form)).short} × {demyLimitsOf(toInputs(form)).long} in;
              anything bigger is Double Demy.
            </div>
            <div className="costing-fields">
              {num('demyPlate', 'Plate & print, Demy', '₹')}
              {num('ddPlate', 'Plate & print, Double Demy', '₹')}
              {num('demyAdditionalRate', 'Additional, Demy', '₹ per unit')}
              {num('ddAdditionalRate', 'Additional, Double Demy', '₹ per unit')}
            </div>

            <div className="costing-fields">
              {num('additionalUnits', 'Additional', 'units')}
              {num('dieCuttingRate', 'Die cutting', '₹ per 1000 sheets')}
              {num('flopPastingRate', 'Flop pasting', '₹ per box')}
              {num('finishCharge', 'Finish', '₹, both sizes')}
            </div>
          </div>
        </div>
        </div>

        {/* The answer, beside the figures that make it rather than
            several screens below them. Drops under the inputs on a
            narrow screen, where the sticky bar carries it instead. */}
        <aside className="costing-aside">
        {/* ------------------------------------------------ the answer */}
        <div className="panel">
          <div className="panel-head"><h2>Cost</h2></div>
          <div className="costing-body">
            {problems.length > 0 && (
              <div className="tag late notice" style={{ marginBottom: 12 }}>
                Check {problems.join(', ')}. A figure below zero still multiplies
                out to a confident-looking cost, so none is shown until it is
                fixed.
              </div>
            )}
            <div className="hint" style={{ marginBottom: 12 }}>
              {missing.length > 0
                ? `Waiting on ${missing.join(', ')}.`
                : r.size === null
                ? 'Enter the board size. Up to 22 × 32 in is costed as Demy, over that as Double Demy.'
                  : `Board ${toInputs(form).boardHeightIn} × ${toInputs(form).boardLengthIn} in is `
                    + (r.size === 'DD'
                      ? 'over 22 × 32 in, so this is costed as Double Demy.'
                      : 'within 22 × 32 in, so this is costed as Demy.')}
            </div>

            <div className="costing-fields costing-totals">
              {sizes.map((s) => (
                <div key={s} className="stat">
                  <div className="stat-label">{SIZE_LABEL[s]} size</div>
                  <div style={{ fontSize: 26, fontWeight: 700, margin: '2px 0 8px' }}>
                    {fig(r[s].total)}
                  </div>
                  <div className="dim">
                    Per box <strong>{figBox(r[s].perBox)}</strong>
                  </div>
                </div>
              ))}
            </div>

            {/*
              Each line is rounded on its own and the totals round the exact sum,
              so the column can read a rupee or two short of the Total -- on the
              worked example, 97,209 against 97,210, because Board and Flute each
              drop a fraction that the exact total keeps.

              DECIDED, not overlooked (16 Oct): the final costs round to the
              nearest figure and the lines are display only. Do not "fix" this by
              summing the rounded lines -- it would change quoted figures, and
              the sheet this came from behaves the same way.
            */}
            <h3 style={{ marginTop: 18 }}>Cost breakdown</h3>
            <table className="tbl" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Particulars</th>
                  {sizes.map((s) => <th key={s} className="num">{SIZE_LABEL[s]}</th>)}
                </tr>
              </thead>
              <tbody>
                {COST_LINES.map((line, i) => (
                  <tr key={line}>
                    <td>
                      <span style={{
                        display: 'inline-block', width: 9, height: 9, borderRadius: 3,
                        background: LINE_COLOURS[i], marginRight: 8,
                      }} />
                      {line}
                    </td>
                    {sizes.map((s) => (
                      <td key={s} className="num">{figLine(r[s].lines[i] ?? 0)}</td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td><strong>Total</strong></td>
                  {sizes.map((s) => (
                    <td key={s} className="num"><strong>{figLine(r[s].total)}</strong></td>
                  ))}
                </tr>
                <tr>
                  <td><strong>Cost per box</strong></td>
                  {sizes.map((s) => (
                    <td key={s} className="num"><strong>{figBox(r[s].perBox)}</strong></td>
                  ))}
                </tr>
              </tbody>
            </table>

            {/* ---------------------------------------- share of the total */}
            {/* Worth drawing only when there is a split to show. With nothing
              entered the whole total is the default finish charge, and a
              full-width bar reading "Finish — 100%" says less than nothing. */}
          {!blocked && r.demy.lines.filter((v) => v > 0).length > 1 && (
              <>
                <h3 style={{ marginTop: 18 }}>Where the money goes</h3>
                <div className="hint" style={{ marginTop: -6, marginBottom: 10 }}>
                  Share of the total.
                </div>
                {sizes.map((s) => (
                  <div key={s} style={{ marginBottom: 10 }}>
                    <div className="dim" style={{ marginBottom: 4 }}>{SIZE_LABEL[s]}</div>
                    <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden' }}>
                      {COST_LINES.map((line, i) => {
                        const share = (r[s].lines[i] ?? 0) / r[s].total;
                        if (!(share > 0)) return null;
                        return (
                          <div key={line} title={`${line} — ${Math.round(share * 100)}%`}
                               style={{ width: `${share * 100}%`, background: LINE_COLOURS[i] }} />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* ---------------------------------------- the sheet itself */}
            <h3 style={{ marginTop: 18 }}>Sheet at a glance</h3>
            <div className="hint" style={{ marginTop: -6, marginBottom: 10 }}>
              Drawn to scale from your board dimensions.
            </div>
            {/* Zeros in four boxes are not information. */}
            {r.sheetAreaM2 > 0 && (
            <div className="costing-fields">
              <div className="stat">
                <div className="stat-label">Sheet area (m²)</div>
                <div>{r.sheetAreaM2.toFixed(3)}</div>
              </div>
              <div className="stat">
                <div className="stat-label">Board weight per sheet (g)</div>
                <div>{inr2.format(r.sheetWeightG)}</div>
              </div>
              <div className="stat">
                <div className="stat-label">Total GSM (with take-up)</div>
                <div>{inr.format(Math.round(r.totalGsm))}</div>
              </div>
              <div className="stat">
                <div className="stat-label">Total board weight (kg)</div>
                <div>{inr2.format(r.totalWeightKg)}</div>
              </div>
            </div>
            )}

            {r.size === null
              ? <div className="dim" style={{ marginTop: 10 }}>
                  Enter the board height and length to see the sheet drawn.
                </div>
              : <SheetDiagram heightIn={toInputs(form).boardHeightIn}
                              lengthIn={toInputs(form).boardLengthIn} />}
          </div>
        </div>
        </aside>
      </div>

      {/* The printed document. Hidden on screen, and the only thing the print
          stylesheet leaves visible -- every .panel above is display:none. */}
      {!blocked && (
        <CostingPrintSheet companyName={companyName} boxName={boxName}
                           variant={variant} preparedBy={user.name}
                           company={company} input={toInputs(form)} r={r} />
      )}

      {/* ------------------------------------------------ history */}
      {currentId !== null && history.length > 0 && (
        <div className="panel no-print">
          <div className="panel-head"><h2>History</h2></div>
          <div className="costing-body">
            <div className="hint" style={{ marginBottom: 10 }}>
              Every change to this costing, newest first. The cost shown is what
              the box came to at the time, worked out again from the figures
              saved then — nothing here was stored as a total.
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What changed</th>
                  <th className="num">Per box</th>
                </tr>
              </thead>
              <tbody>
                {history.map((rev, i) => {
                  const now = revisionCost(rev);
                  // The array is newest first, so the one after it is older.
                  const before = i + 1 < history.length
                    ? revisionCost(history[i + 1]!)
                    : null;
                  const delta = now !== null && before !== null ? now - before : null;
                  return (
                    <tr key={rev.audit_id}>
                      <td className="dim">{whenExact(rev.created_at)}</td>
                      <td>{rev.user_name}</td>
                      <td>
                        {revisionSummary(rev)}
                        {revisionNote(rev) && (
                          <div className="dim" style={{ marginTop: 2 }}>
                            “{revisionNote(rev)}”
                          </div>
                        )}
                      </td>
                      <td className="num">
                        {perBox(now)}
                        {delta !== null && Math.abs(delta) >= 0.005 && (
                          <span className="dim" style={{ marginLeft: 8 }}>
                            {delta > 0 ? '▲' : '▼'} {inr2.format(Math.abs(delta))}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------ deleted */}
      {deleted.length > 0 && (
        <div className="panel no-print">
          <div className="panel-head"><h2>Deleted costings</h2></div>
          <div className="costing-body">
            <div className="hint" style={{ marginBottom: 10 }}>
              Deleted costings stay here with the date they went. Restoring one
              brings it back — unless that box has since been costed again.
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Box</th>
                  <th className="num">Demy total</th>
                  <th className="num">Double Demy total</th>
                  <th>Saved on</th>
                  <th>Deleted</th>
                  {canDelete && <th />}
                </tr>
              </thead>
              <tbody>
                {deleted.map((row) => {
                  // Through the same gate as everywhere else: a costing that
                  // cannot be priced must not show a figure here either.
                  const inp = toInputs(formFromRow(row));
                  const demy = totalOf(inp, 'demy');
                  const dd = totalOf(inp, 'dd');
                  return (
                    <tr key={row.id}>
                      <td>{row.company_name}</td>
                      <td>{row.box_name}</td>
                      <td className="num">{demy === null ? '–' : rs(demy)}</td>
                      <td className="num">{dd === null ? '–' : rs(dd)}</td>
                      <td className="dim">{when(row.updated_at)}</td>
                      <td className="dim">{when(row.deleted_at)}</td>
                      {canDelete && (
                        <td>
                          <button className="btn" disabled={busy}
                                  onClick={() => restore(row)}>Restore</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
